








































import {
  defineComponent,
  ref,
  computed,
  watch,
  useContext,
  useRoute,
} from '@nuxtjs/composition-api'
import { getArticlesTags } from '@/api/bitrix/brand-media/articles/articlesTagsApi'
import { getAuthor } from '@/api/bitrix/brand-media/author/authorApi'
import { getArticleShort } from '@/api/bitrix/brand-media/article/short'

const STORE_NS = 'sections'

type Crumb = { label: string; to?: string | null }

const STATIC_LABELS = {
  main: 'Главная',
  authors: 'Авторы',
  author: 'Автор',
  tags: 'Теги',
} as const

export default defineComponent({
  name: 'BrandMediaBreadcrumbs',
  setup() {
    const ctx = useContext()
    const store = ctx.store
    const route = useRoute()

    const crumbs = ref<Crumb[]>([])
    const showBreadcrumbs = computed(() => {
      return crumbs.value.length > 1
    })

    const isLoaded = computed<boolean>(
      () => store.getters[`${STORE_NS}/IS_LOADED`] ?? false
    )
    const FALLBACK = computed<Record<string, string>>(() => {
      const sectionNames = store.getters[`${STORE_NS}/CODE_NAMES`] ?? {}
      return {
        ...STATIC_LABELS,
        ...sectionNames,
      }
    })

    const capitalize = (s: string) =>
      s ? s.charAt(0).toUpperCase() + s.slice(1) : s
    const humanizeSlug = (slug: string) =>
      decodeURIComponent(String(slug).replace(/-/g, ' '))
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (c) => c.toUpperCase())

    const baseLabel = (base: string) => STATIC_LABELS.main || 'Главная'
    const link = (...parts: string[]) => '/' + parts.filter(Boolean).join('/')

    const lastLabel = computed(() =>
      crumbs.value.length ? crumbs.value[crumbs.value.length - 1].label : ''
    )

    const goBack = () => {
      try {
        if (window && window.history && window.history.length > 1)
          window.history.back()
        else ctx.app.router?.back?.()
      } catch {
        ctx.app.router?.push?.('/')
      }
    }

    const TAGS_LINK_RE = /\/tags(?:$|[/?#])/i
    const makeUnclickableTags = (arr: Crumb[]) =>
      arr.map((c) => (c.to && TAGS_LINK_RE.test(c.to) ? { ...c, to: null } : c))

    async function buildCrumbsFromRoute() {
      if (!isLoaded.value) {
        return makeUnclickableTags([
          { label: baseLabel('prosto'), to: link('prosto') },
        ])
      }

      const dict = FALLBACK.value

      const path = String(route.value.path || '/')
      const parts = path.replace(/^\/+/, '').split('/')

      const base = parts[0] || 'prosto'
      const section = parts[1] || ''
      const slug = parts[2] || ''

      const q = (route.value.query || {}) as Record<string, any>
      const tagCode = route.value.fullPath.split('/').slice(-1)[0]
      const authorId = route.value.fullPath.split('/').slice(-1)[0]

      const items: Crumb[] = [{ label: baseLabel(base), to: link(base) }]

      if (!section || section === 'main') {
        return makeUnclickableTags(items)
      }

      if (section === 'tags') {
        if (!tagCode) {
          items.push({ label: dict.tags || 'Теги', to: null })
          return makeUnclickableTags(items)
        } else {
          items.push({ label: dict.tags || 'Теги', to: null })
          let tagName = tagCode
          try {
            const r = await getArticlesTags(ctx, tagCode)
            tagName = r?.data?.data?.name || tagCode
          } catch {}
          items.push({ label: `#${tagName}`, to: null })
          return makeUnclickableTags(items)
        }
      }

      if (section === 'authors') {
        items.push({ label: dict.authors || 'Авторы', to: null })
        return makeUnclickableTags(items)
      }

      if (section === 'author' && authorId) {
        items.push({
          label: dict.authors || 'Авторы',
          to: link(base, 'authors'),
        })
        let name = `Автор #${authorId}`
        try {
          const r = await getAuthor(ctx, authorId)
          name = r?.data?.data?.name || r?.data?.data?.title || name
        } catch {}
        items.push({ label: name, to: null })
        return makeUnclickableTags(items)
      }

      const sectionLabel = dict[section] || capitalize(section)

      if (!slug) {
        items.push({ label: sectionLabel, to: null })
        return makeUnclickableTags(items)
      }

      items.push({ label: sectionLabel, to: link(base, section) })
      let title = humanizeSlug(slug)
      try {
        const r = await getArticleShort(ctx, slug)
        title = r?.title || title
      } catch {}
      items.push({ label: title, to: null })
      return makeUnclickableTags(items)
    }

    watch(
      () => route.value.fullPath,
      async (v) => {
        crumbs.value = await buildCrumbsFromRoute()
      },
      { immediate: true }
    )

    return { crumbs, showBreadcrumbs, lastLabel, goBack, route }
  },
})
